import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, RefreshCw, Download, Trash2, Loader2, Activity, FileText, Link2, Copy, ExternalLink, Eye, Pencil, Check, X } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ACTION_BADGES = {
  created: "bg-green-500/20 text-green-400",
  uploaded: "bg-blue-500/20 text-blue-400",
  updated: "bg-yellow-500/20 text-yellow-400",
  status_changed: "bg-purple-500/20 text-purple-400",
  deleted: "bg-red-500/20 text-red-400",
};

const ENTITY_BADGES = {
  user: "bg-indigo-500/20 text-indigo-400",
  deal: "bg-cyan-500/20 text-cyan-400",
  campaign: "bg-orange-500/20 text-orange-400",
  rsvp: "bg-pink-500/20 text-pink-400",
};

const LogsTab = ({ token, requestDelete, canEdit = true }) => {
  const [logView, setLogView] = useState("activity");
  const [formLogs, setFormLogs] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [sharedLinks, setSharedLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editingSlug, setEditingSlug] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [savingLabel, setSavingLabel] = useState(false);

  const fetchFormLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/form-logs`, { headers: { Authorization: `Bearer ${token}` } });
      setFormLogs(response.data);
    } catch { /* silent */ }
  }, [token]);

  const fetchActivityLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/activity-logs`, { headers: { Authorization: `Bearer ${token}` } });
      setActivityLogs(response.data);
    } catch { /* silent */ }
  }, [token]);

  const fetchSharedLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/shared-links`, { headers: { Authorization: `Bearer ${token}` } });
      setSharedLinks(response.data || []);
    } catch { /* silent — non-super-admins get 403 */ }
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchFormLogs(), fetchActivityLogs(), fetchSharedLinks()]);
    setLoading(false);
  }, [fetchFormLogs, fetchActivityLogs, fetchSharedLinks]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExportFormLogs = async () => {
    try {
      const response = await axios.get(`${API}/form-logs/export`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'form_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error("Export failed"); }
  };

  const handleDeleteFormLog = (logId) => {
    requestDelete("formlog", logId, "this form log", async () => {
      try {
        await axios.delete(`${API}/form-logs/${logId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFormLogs(prev => prev.filter(l => l.id !== logId));
        toast.success("Log deleted");
      } catch { toast.error("Failed to delete log"); }
    });
  };

  const handleDeleteActivityLog = (logId) => {
    requestDelete("activitylog", logId, "this activity log", async () => {
      try {
        await axios.delete(`${API}/activity-logs/${logId}`, { headers: { Authorization: `Bearer ${token}` } });
        setActivityLogs(prev => prev.filter(l => l.id !== logId));
        toast.success("Log deleted");
      } catch { toast.error("Failed to delete log"); }
    });
  };

  const handleDeleteSharedLink = (slug) => {
    requestDelete("sharedlink", slug, "this shared link (it will be revoked immediately)", async () => {
      try {
        await axios.delete(`${API}/admin/shared-links/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
        setSharedLinks(prev => prev.filter(l => l.slug !== slug));
        toast.success("Link revoked");
      } catch { toast.error("Failed to revoke link"); }
    });
  };

  const startRenameLink = (link) => {
    setEditingSlug(link.slug);
    setEditingLabel(link.label || "");
  };

  const saveLinkLabel = async () => {
    const label = editingLabel.trim();
    if (!label) { toast.error("Playlist name cannot be empty"); return; }
    setSavingLabel(true);
    try {
      await axios.patch(`${API}/admin/shared-links/${editingSlug}`, { label }, { headers: { Authorization: `Bearer ${token}` } });
      setSharedLinks(prev => prev.map(l => l.slug === editingSlug ? { ...l, label } : l));
      toast.success("Playlist renamed");
      setEditingSlug(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setSavingLabel(false);
    }
  };

  const buildShareUrl = (slug) => `${window.location.origin}/gallery/${slug}`;

  const handleCopyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(slug));
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — select the URL manually");
    }
  };

  // Filtered form logs
  const filteredFormLogs = formLogs.filter(log => {
    const q = searchFilter.toLowerCase();
    return !q || [log.name, log.email, log.company, log.ip_address, log.location].some(v => (v || "").toLowerCase().includes(q));
  });

  // Filtered activity logs
  const filteredActivityLogs = activityLogs.filter(log => {
    const q = searchFilter.toLowerCase();
    const matchesSearch = !q || [log.details, log.actor_email, log.entity_type, log.action].some(v => (v || "").toLowerCase().includes(q));
    const matchesType = typeFilter === "all" || log.entity_type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Filtered shared links (newest first)
  const filteredSharedLinks = [...sharedLinks]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .filter(link => {
      const q = searchFilter.toLowerCase();
      return !q || [link.label, link.created_by, link.slug].some(v => (v || "").toLowerCase().includes(q));
    });

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6" data-testid="logs-header">
        <div>
          <h2 className="text-2xl font-bold text-white font-mono">Logs</h2>
          <p className="text-[#ebebeb]/60 mt-1 font-mono text-sm">Track form submissions and platform activity</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchAll} className="p-2 text-[#ebebeb]/60 hover:text-white rounded-lg hover:bg-white/5" data-testid="refresh-logs-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          {logView === "forms" && (
            <button onClick={handleExportFormLogs} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-[#ebebeb]/60 hover:text-white hover:bg-white/5 text-xs font-mono" data-testid="export-logs-btn"><Download className="w-3.5 h-3.5" /> Export CSV</button>
          )}
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: "activity", label: "Activity Log", icon: Activity, count: activityLogs.length },
          { id: "forms", label: "Form Submissions", icon: FileText, count: formLogs.length },
          { id: "sharedlinks", label: "Shared Links", icon: Link2, count: sharedLinks.length },
        ].map(view => (
          <button key={view.id} onClick={() => { setLogView(view.id); setSearchFilter(""); setTypeFilter("all"); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-sm transition-colors ${logView === view.id ? 'bg-[#ff3737] text-white' : 'border border-white/10 text-[#ebebeb]/60 hover:bg-white/5'}`}
            data-testid={`log-view-${view.id}`}>
            <view.icon className="w-3.5 h-3.5" /> {view.label} <span className="text-xs opacity-60">({view.count})</span>
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/40" />
          <input
            type="text"
            placeholder={
              logView === "activity"
                ? "Search by details, email, or type..."
                : logView === "sharedlinks"
                ? "Search by label, creator, or slug..."
                : "Search by name, email, or company..."
            }
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="form-input w-full pl-10"
            data-testid="logs-search"
          />
        </div>
        {logView === "activity" && (
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="form-input text-sm" style={{ width: 'auto' }} data-testid="logs-type-filter">
            <option value="all">All Types</option>
            <option value="user">Users</option>
            <option value="deal">Deals</option>
            <option value="campaign">Campaigns</option>
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#ff3737]" /></div>
      ) : logView === "activity" ? (
        /* Activity Logs View */
        <Card className="bg-[#1e2124] border-white/10">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Date</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Action</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Type</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Details</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">By</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivityLogs.length > 0 ? filteredActivityLogs.map(log => (
                  <TableRow key={log.id} className="border-white/10" data-testid={`activity-log-${log.id}`}>
                    <TableCell className="text-[#ebebeb]/80 text-xs font-mono whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</TableCell>
                    <TableCell><Badge className={`text-xs border-0 capitalize ${ACTION_BADGES[log.action] || "bg-gray-500/20 text-gray-400"}`}>{log.action?.replace("_", " ")}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs border-0 capitalize ${ENTITY_BADGES[log.entity_type] || "bg-gray-500/20 text-gray-400"}`}>{log.entity_type}</Badge></TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-sm max-w-[400px] truncate" title={log.details}>{log.details}</TableCell>
                    <TableCell className="text-[#ebebeb]/50 text-xs font-mono">{log.actor_email}</TableCell>
                    <TableCell>{canEdit && <button onClick={() => handleDeleteActivityLog(log.id)} className="p-1 text-[#ebebeb]/30 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-activity-log-${log.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#ebebeb]/40 py-12 font-mono">{searchFilter || typeFilter !== "all" ? "No matching activity logs" : "No activity recorded yet"}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : logView === "forms" ? (
        /* Form Logs View */
        <Card className="bg-[#1e2124] border-white/10" data-testid="forms-logs-table-card">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Date</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Name</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Email</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Company</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">DSP</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Seat #</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">IP Address</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Location</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Trigger</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Status</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFormLogs.length > 0 ? filteredFormLogs.map(log => (
                  <TableRow key={log.id} className="border-white/10" data-testid={`formlog-row-${log.id}`}>
                    <TableCell className="text-[#ebebeb]/80 text-xs font-mono whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</TableCell>
                    <TableCell className="text-white text-sm">{log.name || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-sm">{log.email || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-sm">{log.company || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-sm">{log.dsp || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-sm">{log.dsp_seat_number || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/50 text-xs font-mono">{log.ip_address || "-"}</TableCell>
                    <TableCell className="text-[#ebebeb]/80 text-xs">{log.location || "-"}</TableCell>
                    <TableCell><Badge className={`text-xs border-0 ${log.trigger === 'step1_next' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{log.trigger === 'step1_next' ? 'Step 1 Next' : 'Modal Close'}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs border-0 ${log.submitted ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{log.submitted ? 'Submitted' : 'Abandoned'}</Badge></TableCell>
                    <TableCell>{canEdit && <button onClick={() => handleDeleteFormLog(log.id)} className="p-1 text-[#ebebeb]/30 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-formlog-${log.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={11} className="text-center text-[#ebebeb]/40 py-12 font-mono">{searchFilter ? "No matching form logs" : "No form logs recorded yet"}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* Shared Links View */
        <Card className="bg-[#1e2124] border-white/10" data-testid="shared-links-table-card">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Created</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Playlist Name</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs"># Ads</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Views</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Public URL</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs">Created By</TableHead>
                  <TableHead className="text-[#ebebeb]/60 font-mono text-xs w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSharedLinks.length > 0 ? filteredSharedLinks.map(link => (
                  <TableRow key={link.slug} className="border-white/10" data-testid={`shared-link-row-${link.slug}`}>
                    <TableCell className="text-[#ebebeb]/80 text-xs font-mono whitespace-nowrap">
                      {link.created_at ? new Date(link.created_at).toLocaleString() : "-"}
                    </TableCell>
                    <TableCell className="text-white text-sm max-w-[280px]">
                      {editingSlug === link.slug ? (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveLinkLabel();
                              if (e.key === "Escape") setEditingSlug(null);
                            }}
                            maxLength={120}
                            className="flex-1 bg-white/5 border border-white/15 rounded px-2 py-1 text-xs font-mono text-white focus:outline-none focus:border-[#ff3737]"
                            data-testid={`shared-link-label-input-${link.slug}`}
                          />
                          <button onClick={saveLinkLabel} disabled={savingLabel} className="p-1 text-green-400 hover:text-green-300" data-testid={`shared-link-label-save-${link.slug}`}>
                            {savingLabel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => setEditingSlug(null)} className="p-1 text-[#ebebeb]/40 hover:text-white" data-testid={`shared-link-label-cancel-${link.slug}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 group">
                          <span className="truncate" title={link.label}>{link.label || `${(link.ad_urls || []).length} ads`}</span>
                          {canEdit && (
                            <button
                              onClick={() => startRenameLink(link)}
                              className="p-1 text-[#ebebeb]/30 hover:text-[#ff3737] transition-colors"
                              title="Rename playlist"
                              data-testid={`shared-link-rename-${link.slug}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="text-xs border-0 bg-cyan-500/20 text-cyan-400">
                        {(link.ad_urls || []).length}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-[#ebebeb]/70 text-xs font-mono">
                        <Eye className="w-3 h-3" />
                        {link.views || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <code className="text-[10px] font-mono text-[#ebebeb]/60 bg-white/5 px-2 py-0.5 rounded" title={buildShareUrl(link.slug)}>
                        /gallery/{link.slug}
                      </code>
                    </TableCell>
                    <TableCell className="text-[#ebebeb]/50 text-xs font-mono">{link.created_by || "-"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button
                        onClick={() => handleCopyLink(link.slug)}
                        className="p-1 text-[#ebebeb]/50 hover:text-[#ff3737] transition-colors"
                        title="Copy public URL"
                        data-testid={`copy-shared-link-${link.slug}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={buildShareUrl(link.slug)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block p-1 text-[#ebebeb]/50 hover:text-[#ff3737] transition-colors ml-1"
                        title="Open in new tab"
                        data-testid={`open-shared-link-${link.slug}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button
                        onClick={() => handleDeleteSharedLink(link.slug)}
                        className={`p-1 text-[#ebebeb]/30 hover:text-red-400 transition-colors ml-1 ${canEdit ? "" : "hidden"}`}
                        title="Revoke link"
                        data-testid={`delete-shared-link-${link.slug}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#ebebeb]/40 py-12 font-mono">
                      {searchFilter ? "No matching shared links" : "No shared links created yet"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default LogsTab;
